import { Button } from '@components/button'
import { WroomImage } from '@components/wroom-image'
import { ProductImageCarousel } from '@haus-storefront-react/product-image-carousel'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { cssVars } from '../../utilities/css-vars'
import styles from './ImageCarousel.module.scss'

const DEFAULT_MAX_VISIBLE_THUMBNAILS = 5
const MIN_VISIBLE_THUMBNAILS = 1
const MAX_VISIBLE_THUMBNAILS = 12

interface ImageCarouselAsset {
  id: string | number
  source: string
}

interface ThumbnailStripProps {
  assets: ImageCarouselAsset[]
  currentActiveSlide: number
  handleSetActiveSlide: (nextSlide: number) => void
  maxVisibleThumbnails?: number
}

function clampMaxVisibleThumbnails(value?: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_MAX_VISIBLE_THUMBNAILS
  }

  return Math.min(MAX_VISIBLE_THUMBNAILS, Math.max(MIN_VISIBLE_THUMBNAILS, Math.floor(value)))
}

function getScrollBehavior(): ScrollBehavior {
  if (typeof window === 'undefined') {
    return 'auto'
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}

/**
 * Render product image thumbnails with optional horizontal scrolling when items exceed the visible limit.
 */
export const ThumbnailStrip = ({
  assets,
  currentActiveSlide,
  handleSetActiveSlide,
  maxVisibleThumbnails,
}: ThumbnailStripProps) => {
  const { t } = useTranslation()
  const viewportRef = useRef<HTMLDivElement>(null)
  const thumbListRef = useRef<HTMLDivElement>(null)
  const thumbnailListId = useId()

  const visibleThumbCount = clampMaxVisibleThumbnails(maxVisibleThumbnails)
  const thumbViewportCount = Math.max(1, Math.min(visibleThumbCount, assets.length))
  const hasMultipleAssets = assets.length > 1

  const goToPrevThumbnail = () => {
    if (!hasMultipleAssets) {
      return
    }

    const nextSlide = currentActiveSlide === 0 ? assets.length - 1 : currentActiveSlide - 1
    handleSetActiveSlide(nextSlide)
  }

  const goToNextThumbnail = () => {
    if (!hasMultipleAssets) {
      return
    }

    const nextSlide = currentActiveSlide === assets.length - 1 ? 0 : currentActiveSlide + 1
    handleSetActiveSlide(nextSlide)
  }

  useEffect(() => {
    const activeThumb = thumbListRef.current?.querySelector<HTMLElement>(
      `[data-thumb-index="${currentActiveSlide}"]`,
    )

    if (!activeThumb) {
      return
    }

    activeThumb.scrollIntoView({
      behavior: getScrollBehavior(),
      block: 'nearest',
      inline: 'nearest',
    })
  }, [currentActiveSlide])

  const thumbnailButtons = assets.map((asset, index) => (
    <Button
      key={asset.id}
      onClick={() => handleSetActiveSlide(index)}
      variant="ghost"
      data-active={index === currentActiveSlide}
      data-thumb-index={index}
      className={styles.productCarouselThumbButton}
      aria-pressed={index === currentActiveSlide}
      aria-label={t('a11y.imageCarousel.aria.viewImageOfTotal', {
        imageIndex: index + 1,
        totalImages: assets.length,
      })}
    >
      <WroomImage
        src={asset.source}
        alt={t('a11y.imageCarousel.aria.productThumbnail', { imageIndex: index + 1 })}
        className={styles.productCarouselThumb}
        width={60}
        height={60}
        ratio="1:1"
      />
    </Button>
  ))

  const thumbList = (
    <ProductImageCarousel.ThumbWrapper
      id={thumbnailListId}
      ref={thumbListRef}
      className={styles.productCarouselThumbs}
      aria-label={t('a11y.imageCarousel.aria.viewProductImages')}
      data-active-slide={currentActiveSlide}
      role="group"
    >
      {thumbnailButtons}
    </ProductImageCarousel.ThumbWrapper>
  )

  return (
    <div
      className={styles.thumbnailNavigation}
      style={cssVars({
        '--haus-imageCarousel__thumbViewport__max-visible-thumbnails': thumbViewportCount,
      })}
    >
      <button
        type="button"
        className={styles.thumbnailStepButton}
        onClick={goToPrevThumbnail}
        aria-label={t('a11y.imageCarousel.aria.previousImage')}
        aria-controls={thumbnailListId}
        disabled={!hasMultipleAssets}
      >
        <ChevronLeftIcon width={20} height={20} aria-hidden />
      </button>
      <div ref={viewportRef} className={`${styles.thumbViewport} ${styles.thumbViewportStepped}`}>
        {thumbList}
      </div>
      <button
        type="button"
        className={styles.thumbnailStepButton}
        onClick={goToNextThumbnail}
        aria-label={t('a11y.imageCarousel.aria.nextImage')}
        aria-controls={thumbnailListId}
        disabled={!hasMultipleAssets}
      >
        <ChevronRightIcon width={20} height={20} aria-hidden />
      </button>
    </div>
  )
}
